/**
 * frontend/src/pages/ClaimVerify.jsx — /claim/:artistId/verify (signed-in creators)
 * SoundDAO Sprint 6A Prompt 6 — the Verify step. Creates (or resumes) the claim; the
 * official-domain email is checked automatically on load; "Check now" re-runs every method
 * this band's frozen official links support.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { checkClaim, clearPendingClaim, startClaim } from '../lib/archiveApi';
import { track } from '../lib/analytics';
import '../styles/archive.css';

const REASONS = {
  email_not_confirmed: 'Your sign-in email isn’t confirmed yet.',
  free_mail_domain: 'Your sign-in email is a personal address (Gmail, iCloud…), so it can’t prove the band’s domain.',
  domain_mismatch: 'Your sign-in email isn’t on the band’s official domain.',
  code_not_found: 'We couldn’t find the code on your page yet.',
  no_email: 'No email on your account.',
};

function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

export default function ClaimVerify({ user }) {
  const { artistId } = useParams();
  const [claim, setClaim] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(() => {
    setError(null);
    startClaim(artistId).then((d) => setClaim(d.claim)).catch((e) => setError(e));
  }, [artistId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (claim?.status === 'verified' || error?.code === 'ALREADY_CLAIMED') clearPendingClaim();
  }, [claim, error]);

  const check = async () => {
    setChecking(true);
    setNote(null);
    try {
      const d = await checkClaim(claim.id);
      setClaim(d.claim);
      for (const [method, r] of Object.entries(d.claim.checks || {})) {
        track('claim_verify_attempt', { method, result: r.verified ? 'verified' : (r.reason || 'not_verified') });
      }
      if (d.claim.status === 'verified' && claim.status !== 'verified') track('claim_verified', { method: d.claim.method || 'unknown' });
      if (d.claim.status !== 'verified') {
        const reasons = Object.values(d.claim.checks || {}).map((r) => REASONS[r.reason] || null).filter(Boolean);
        setNote(reasons.join(' ') || 'Not verified yet.');
      }
    } catch (e) {
      setNote(e.code === 'RATE_LIMITED' && e.body?.retryAt
        ? `${e.message} (after ${new Date(e.body.retryAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})`
        : e.message);
    } finally {
      setChecking(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(claim.code); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { window.prompt('Copy your code:', claim.code); }
  };

  if (error) {
    return (
      <div className="ax-page"><div className="ax-wrap">
        <h1 className="ax-h1">Claim your profile</h1>
        {error.code === 'CREATOR_REQUIRED' ? (
          <div className="ax-card">
            <p className="ax-sub" style={{ margin: 0 }}>
              {user?.persona && user.persona !== 'creator'
                ? 'Claims are made from a creator account, and this account is set up as a fan. Sign in with the band’s email to create a creator account.'
                : 'Finish your creator profile first — then come back here to verify.'}
            </p>
            {(!user?.persona || user.persona === 'creator') && <div className="ax-actions"><Link className="ax-btn ax-btn--primary" to="/creator-onboarding">Set up my creator profile</Link></div>}
          </div>
        ) : (
          <div className="ax-banner ax-banner--error"><span>{error.message}</span>{error.code !== 'ALREADY_CLAIMED' && <button type="button" className="ax-btn ax-btn--sm" onClick={load}>Retry</button>}</div>
        )}
      </div></div>
    );
  }
  if (!claim) return <div className="ax-page"><div className="ax-wrap"><div className="ax-skel" style={{ height: 160 }} /><div className="ax-skel" /></div></div>;

  if (claim.status === 'verified') {
    const n = claim.nominatorCount || 0;
    return (
      <div className="ax-page"><div className="ax-wrap">
        <div className="ax-card" style={{ borderColor: 'rgba(100,255,176,.5)' }}>
          <div className="ax-ok" style={{ fontWeight: 800, fontSize: 18 }}>✓ Verified — {claim.artistName} is on SoundDAO</div>
          <p className="ax-sub" style={{ marginTop: 8, color: '#fff' }}>
            {n > 0 ? `${n} fan${n === 1 ? '' : 's'} found your music in the Archive before you got here.` : 'Your profile is linked to your Archive recordings.'}
          </p>
          <div className="ax-actions"><Link className="ax-btn ax-btn--primary" to="/dashboard/creator">Go to my dashboard</Link></div>
        </div>
      </div></div>
    );
  }
  if (claim.status === 'expired') {
    return <div className="ax-page"><div className="ax-wrap"><div className="ax-banner">This claim expired after 30 days. <Link to={`/claim/${artistId}`} style={{ color: '#fff' }}>Start again</Link></div></div></div>;
  }

  const homepages = claim.links.filter((l) => l.type === 'homepage');
  const pages = claim.links;
  const hasDomain = claim.methods.includes('domain_email');
  const hasPage = claim.methods.includes('page_code');

  return (
    <div className="ax-page">
      <div className="ax-wrap">
        <h1 className="ax-h1">Verify {claim.artistName}</h1>
        <p className="ax-sub">Any one of these proves it’s you. It’s automatic — nobody at SoundDAO reviews it.</p>

        {!claim.methods.length && (
          <div className="ax-banner">
            We couldn&#39;t find an official website or Bandcamp page for you yet.
          </div>
        )}

        {hasDomain && (
          <div className="ax-card">
            <div className="ax-title">1 · Sign in with your official email</div>
            <p className="ax-meta" style={{ fontSize: 13 }}>
              An address at {homepages.map((l) => hostOf(l.url)).join(' or ')} (e.g. booking@{hostOf(homepages[0].url)}) verifies you instantly.
              {claim.checks?.domain_email && !claim.checks.domain_email.verified && <> {REASONS[claim.checks.domain_email.reason] || ''}</>}
            </p>
          </div>
        )}

        {hasPage && (
          <div className="ax-card">
            <div className="ax-title">{hasDomain ? '2' : '1'} · Put this code on your official page</div>
            <div className="ax-row" style={{ marginTop: 10 }}>
              <div className="ax-code ax-grow">{claim.code}</div>
              <button type="button" className="ax-btn ax-btn--sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <ol className="ax-steps">
              <li>Add it anywhere visible on {pages.map((l, i) => <React.Fragment key={l.url}>{i ? ' or ' : ''}<a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: '#a5b4fc' }}>{hostOf(l.url)}</a></React.Fragment>)} — a bio or About section is fine.</li>
              <li>Tap <b>Check now</b>. You can remove the code once you’re verified.</li>
            </ol>
            <p className="ax-meta">We also re-check once a day for 30 days (until {new Date(claim.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}).</p>
          </div>
        )}

        {claim.methods.length > 0 && (
          <div className="ax-actions">
            <button type="button" className="ax-btn ax-btn--primary" onClick={check} disabled={checking}>{checking ? 'Checking…' : 'Check now'}</button>
            <span className="ax-meta">Only links on your profile before SoundDAO found you count.</span>
          </div>
        )}
        {note && <div className="ax-banner" style={{ marginTop: 12 }}>{note}</div>}
        <p className="ax-attr" style={{ marginTop: 18 }}>Already claimed by someone else? Contact support.</p>
      </div>
    </div>
  );
}
